import React, { useState, useRef, useImperativeHandle } from 'react';
import { FocusOn } from 'react-focus-on';
import classnames from 'classnames';
import { TextLink, NotificationBadge } from '../index';
import styles from './header.module.scss';
import { ButtonType, LinkType } from '../types';
import { SecondLevel } from './second-level';
import { CampaignCardListType } from './campaign-card-list';
import { useScrollTop } from './use-scroll-top';

export type NavigationItemType = (
  | ButtonType
  | LinkType
  | {
      firstLevellabel: string;
      secondLevelOverviewLink: LinkType;
      secondLevelSubItems: LinkType[];
      campaign?: CampaignCardListType;
    }
)[];

export type HeaderType = {
  navigationAriaLabel?: string;
  openMenuAriaLabel: string;
  closeMenuAriaLabel: string;
  backToFirstLevelAriaLabel?: string;
  logoLink: LinkType;
  navigationItems?: NavigationItemType;
  className?: string;
  cta?: LinkType;
  myGarageLink?: LinkType;
  myGarageBadge?: number;
  myShoppingBadge?: number;
  myShoppingLink?: LinkType;
  myAccountLink?: LinkType;
  isInverted: boolean;
  isLoggedIn?: boolean;
};

export type HeaderRef = {
  readonly closeMenu: () => void;
};

const isLinkNavigationItem = (props: unknown): props is LinkType =>
  typeof (props as LinkType)?.label === 'string' && typeof (props as LinkType)?.href === 'string';

const isButtonNavigationItem = (props: unknown): props is ButtonType =>
  typeof (props as ButtonType)?.label === 'string' && typeof (props as ButtonType)?.onClick !== 'undefined';

/*
++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
 TO DO:
  - refactor the (boolean | number) logic for opened levels.
  - The scrollTop functions for the <ul> elements
  (secondLevel + campaign) only need to fire on desktop.
  - There seems to be a re-render on the first item 
  that has a secondLevelList
  - animation when second list with campaign closes. 
  the gradient layer is not animated.
  - reduce unused css.
++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ 
++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
*/

export const Header = React.forwardRef<HeaderRef, HeaderType>(
  (
    {
      className,
      cta,
      navigationItems,
      navigationAriaLabel,
      logoLink,
      openMenuAriaLabel,
      closeMenuAriaLabel,
      backToFirstLevelAriaLabel = closeMenuAriaLabel,
      myAccountLink,
      myShoppingLink,
      myGarageLink,
      isInverted,
      myGarageBadge,
      myShoppingBadge,
      isLoggedIn
    },
    ref
  ) => {
    const [flyoutIsOpen, setFlyoutIsOpen] = useState<boolean>(false);
    const [openedSecondLevelIndex, setOpenedSecondLevelIndex] = useState<number | false>(false);
    const [secondLevelHasCampaign, setSecondLevelHasCampaign] = useState<boolean>(false);
    const firstLevelListRef = useRef<HTMLUListElement>(null);

    const classes = classnames(
      styles.header,
      className,
      {
        [styles.withoutNavigation]: !navigationItems || navigationItems.length === 0,
        [styles.open]: flyoutIsOpen,
        [styles.secondLevelIsOpen]: openedSecondLevelIndex || openedSecondLevelIndex === 0,
        [styles.hasCampaign]: secondLevelHasCampaign,
        [styles.isInverted]: isInverted
      },
      'setting--inverted'
    );

    const generateIconButton = (): {
      ariaLabel: string;
      iconClass: string;
      onClick: React.MouseEventHandler<HTMLButtonElement>;
    } => {
      if (flyoutIsOpen && openedSecondLevelIndex === false) {
        return {
          ariaLabel: closeMenuAriaLabel,
          iconClass: 'close',
          onClick: () => setFlyoutIsOpen(false)
        };
      }

      if (openedSecondLevelIndex || openedSecondLevelIndex === 0) {
        return {
          ariaLabel: backToFirstLevelAriaLabel,
          iconClass: 'chevronLeft',
          onClick: () => setOpenedSecondLevelIndex(false)
        };
      }

      return { ariaLabel: openMenuAriaLabel, iconClass: 'burgerMenu', onClick: () => setFlyoutIsOpen(true) };
    };

    const closeMenu = React.useCallback(() => {
      setOpenedSecondLevelIndex(false);
      setFlyoutIsOpen(false);
    }, []);

    useImperativeHandle<HeaderRef, HeaderRef>(ref, () => ({ closeMenu }), [closeMenu]);

    const menuButton = generateIconButton();

    useScrollTop(firstLevelListRef, flyoutIsOpen);

    return (
      <header className={classes} aria-label={navigationAriaLabel} data-nosnippet="true" data-testid="header-component">
        <FocusOn enabled={flyoutIsOpen} onEscapeKey={closeMenu} onClickOutside={closeMenu}>
          <div className={styles.inner}>
            {navigationItems && navigationItems.length !== 0 && (
              <>
                <button
                  aria-label={menuButton.ariaLabel}
                  onClick={menuButton.onClick}
                  aria-expanded={flyoutIsOpen}
                  aria-controls="smart-header-id"
                  className={classnames(styles.menuButton, styles[menuButton.iconClass])}
                />
                <nav className={styles.flyout} id="smart-header-id" aria-hidden={!flyoutIsOpen}>
                  <ul className={styles.list} ref={firstLevelListRef}>
                    {navigationItems.map((item, index) => {
                      if (isLinkNavigationItem(item)) {
                        return (
                          <li key={item.label + index} className={styles.item}>
                            <TextLink
                              {...item}
                              variant="hyperlink-100"
                              className={classnames(styles.link, { [styles.isExternal]: item.isExternal })}
                              linkText={item.label}
                            />
                          </li>
                        );
                      }

                      if (isButtonNavigationItem(item)) {
                        return (
                          <li key={item.label + index} className={styles.item}>
                            <button onClick={item.onClick} className={styles.firstLevelButtonItem}>
                              {item.label}
                            </button>
                          </li>
                        );
                      }

                      const open = openedSecondLevelIndex === index;

                      return (
                        <li key={item.firstLevellabel + index} className={styles.item}>
                          <button
                            className={classnames(styles.navItemButton, {
                              [styles.isActive]: open
                            })}
                            onClick={() => {
                              setOpenedSecondLevelIndex(index);
                              setSecondLevelHasCampaign(Boolean(item.campaign));
                            }}
                          >
                            {item.firstLevellabel}
                          </button>
                          <SecondLevel
                            isOpen={open}
                            overviewLink={item.secondLevelOverviewLink}
                            items={item.secondLevelSubItems}
                            campaign={item.campaign}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </>
            )}
            <a
              aria-label={logoLink.label}
              className={styles.logoLink}
              href={logoLink.href}
              rel={logoLink.rel}
              target={logoLink.target}
              onClick={logoLink.onClick}
            ></a>
            {(myAccountLink || myGarageLink || myShoppingLink) && (
              <div className={classnames(styles.garageAccountContainer)}>
                {myGarageLink && (
                  <div className={styles.iconContainer}>
                    <a
                      className={styles.garageLink}
                      href={myGarageLink.href}
                      aria-label={myGarageLink.label}
                      onClick={myGarageLink.onClick}
                      target={myGarageLink.target}
                      rel={myGarageLink.rel}
                    />
                    {!!myGarageBadge && (
                      <NotificationBadge
                        variant="200"
                        className={classnames(styles.badge, styles.garageBadge)}
                        value={myGarageBadge}
                      />
                    )}
                  </div>
                )}
                {myAccountLink && (
                  <div className={classnames(styles.iconContainer, { [styles.loggedInIndicator]: isLoggedIn })}>
                    <a
                      className={styles.accountLink}
                      href={myAccountLink.href}
                      aria-label={myAccountLink.label}
                      onClick={myAccountLink.onClick}
                      target={myAccountLink.target}
                      rel={myAccountLink.rel}
                    />
                  </div>
                )}
                {myShoppingLink && (
                  <div className={styles.iconContainer}>
                    <a
                      className={styles.shoppingLink}
                      href={myShoppingLink.href}
                      aria-label={myShoppingLink.label}
                      onClick={myShoppingLink.onClick}
                      target={myShoppingLink.target}
                      rel={myShoppingLink.rel}
                    />
                    {!!myShoppingBadge && (
                      <NotificationBadge
                        variant="200"
                        value={myShoppingBadge}
                        className={classnames(styles.badge, styles.shoppingBadge)}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
            {cta && (
              <TextLink {...cta} variant="button-primary" className={styles.cta} size="200" linkText={cta.label} />
            )}
          </div>
        </FocusOn>
        <div className={classnames(styles.layer, { [styles.open]: flyoutIsOpen })} />
      </header>
    );
  }
);

Header.displayName = 'Header';
